import { Card, CardContent, CardMedia, Typography, Button, Box, Chip, Stack } from '@mui/material';
import { Create } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { getFileUrl } from '../services/api';
import { Template } from '../types/template';
import { useNavigate } from 'react-router-dom';

interface GalleryTemplateProps {
  template: Template;
  onUseTemplate: (id: string) => void;
  viewMode: 'grid' | 'list';
}

const GalleryTemplate = ({ template, onUseTemplate, viewMode }: GalleryTemplateProps) => {
  const navigate = useNavigate();

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <Card 
        sx={{ 
          height: '100%', 
          display: 'flex', 
          flexDirection: viewMode === 'grid' ? 'column' : 'row',
          borderRadius: 2,
          overflow: 'hidden',
          cursor: 'pointer',
          '&:hover': {
            boxShadow: 6,
            transform: 'translateY(-4px)',
            transition: 'all 0.2s ease-in-out',
          },
        }}
        onClick={() => navigate(`/templates/${template.id}`)}
      >
        <CardMedia
          component="img"
          sx={{ 
            height: viewMode === 'grid' ? 200 : '100%',
            width: viewMode === 'grid' ? '100%' : 200,
            objectFit: 'cover',
          }}
          image={getFileUrl(template.imageUrl)}
          alt={template.title}
        />
        <CardContent 
          sx={{ 
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            p: 3,
          }}
        >
          <Box>
            <Typography gutterBottom variant="h6" component="h2" fontWeight="bold">
              {template.title}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {template.description}
            </Typography>
            {template.metadata?.tags && (
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {template.metadata.tags.map((tag) => (
                  <Chip
                    key={tag}
                    label={tag}
                    size="small"
                    sx={{ 
                      bgcolor: 'primary.light',
                      color: 'primary.contrastText',
                      '&:hover': {
                        bgcolor: 'primary.main',
                      },
                    }}
                  />
                ))}
              </Stack>
            )}
          </Box>
          <Box sx={{ mt: 2 }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<Create />}
              onClick={(e) => {
                e.stopPropagation();
                onUseTemplate(template.id);
              }}
              fullWidth={viewMode === 'grid'}
              size="large"
            >
              Use Template
            </Button>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default GalleryTemplate; 